import {
  type AiTakeAVideoConfig,
  type AttendanceOnDemandIntegrationConfig,
  AttendanceVerificationMethod,
  type AttendanceVerificationMethodConfig,
  type MeshIntegrationConfig,
  type NfcTagRequestStatus,
  type TakeAVideoConfig,
  type UkgIntegrationConfig,
  type UkgReadyIntegrationConfig,
} from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { z } from "zod";

export interface NfcTag {
  tagId: string;
  tagNumber: number;
  tagStatus: keyof typeof NfcTagRequestStatus;
  tagLocation: string;
}

export interface SaveNfcTagLocationParams {
  tagId: string;
  location: string;
  currentStatus: keyof typeof NfcTagRequestStatus;
}

export type AttendanceVerificationMethodType =
  (typeof AttendanceVerificationMethod)[keyof typeof AttendanceVerificationMethod];

export const EXTERNAL_INTEGRATION_VENDOR = {
  UKG: AttendanceVerificationMethod.UKG_INTEGRATION,
  UKG_READY: AttendanceVerificationMethod.UKG_READY_INTEGRATION,
  ATTENDANCE_ON_DEMAND: AttendanceVerificationMethod.ATTENDANCE_ON_DEMAND_INTEGRATION,
  MESH: AttendanceVerificationMethod.MESH_INTEGRATION,
} as const;

export type ExternalIntegrationVendor =
  (typeof EXTERNAL_INTEGRATION_VENDOR)[keyof typeof EXTERNAL_INTEGRATION_VENDOR];

export const TUTORIAL_SECTION = {
  NAVIGATION: "navigation",
  TIMECLOCK_USE: "timeclock-use",
} as const;

export type TutorialSection = (typeof TUTORIAL_SECTION)[keyof typeof TUTORIAL_SECTION];

export const tutorialStepSchema = z.object({
  tutorialBody: z.string(),
  tutorialHeadline: z.string(),
  tutorialImage: z.string(),
  tutorialSection: z.string(),
});

export type TutorialStep = z.infer<typeof tutorialStepSchema>;

export const tutorialSchema = z.object({
  stepsClockIn: z.array(tutorialStepSchema),
  stepsClockOut: z.array(tutorialStepSchema),
});

export type Tutorial = z.infer<typeof tutorialSchema>;

export const ATTENDANCE_VERIFICATION_METHOD_DISPLAY = {
  [AttendanceVerificationMethod.GEOFENCE]: {
    label: "Geofence",
    description: "Workers clock in/out based on their GPS location",
  },
  [AttendanceVerificationMethod.NFC_TAG]: {
    label: "Clipboard Health NFC time clock posters",
    description: "Workers scan NFC tags to clock in/out",
  },
  [AttendanceVerificationMethod.UKG_INTEGRATION]: {
    label: "External integration",
    description: "Workers use an external timeclock system",
  },
  [AttendanceVerificationMethod.UKG_READY_INTEGRATION]: {
    label: "External integration",
    description: "Workers use an external timeclock system",
  },
  [AttendanceVerificationMethod.ATTENDANCE_ON_DEMAND_INTEGRATION]: {
    label: "External integration",
    description: "Workers use an external timeclock system",
  },
  [AttendanceVerificationMethod.MESH_INTEGRATION]: {
    label: "External integration",
    description: "Workers use an external timeclock system",
  },
  [AttendanceVerificationMethod.TAKE_A_VIDEO]: {
    label: "Take a video of workplace time clock",
    description: "Workers record a video of themselves using the facility timeclock",
  },
  [AttendanceVerificationMethod.AI_TAKE_A_VIDEO]: {
    label: "AI Take a video",
    description: "Workers record a video validated by AI",
  },
} as const;

/**
 * Methods that require workerCredentialConfiguration.timekeeping to be enabled.
 */
export const TIMEKEEPING_METHODS_REQUIRING_CREDENTIALS: AttendanceVerificationMethod[] = [
  AttendanceVerificationMethod.UKG_INTEGRATION,
  AttendanceVerificationMethod.UKG_READY_INTEGRATION,
  AttendanceVerificationMethod.ATTENDANCE_ON_DEMAND_INTEGRATION,
  AttendanceVerificationMethod.TAKE_A_VIDEO,
  AttendanceVerificationMethod.AI_TAKE_A_VIDEO,
];

export function hasEnabledMethodThatRequiresCredentials(
  methods: readonly AttendanceVerificationMethodConfig[]
): boolean {
  return methods.some(
    (method) => method.isEnabled && TIMEKEEPING_METHODS_REQUIRING_CREDENTIALS.includes(method.type)
  );
}

export function isExternalIntegrationType(
  type: AttendanceVerificationMethodType
): type is ExternalIntegrationVendor {
  return (
    type === AttendanceVerificationMethod.UKG_INTEGRATION ||
    type === AttendanceVerificationMethod.UKG_READY_INTEGRATION ||
    type === AttendanceVerificationMethod.ATTENDANCE_ON_DEMAND_INTEGRATION ||
    type === AttendanceVerificationMethod.MESH_INTEGRATION
  );
}

export function hasTutorialConfig(
  config: AttendanceVerificationMethodConfig
): config is
  | UkgIntegrationConfig
  | UkgReadyIntegrationConfig
  | AttendanceOnDemandIntegrationConfig
  | TakeAVideoConfig
  | AiTakeAVideoConfig {
  return (
    config.type === AttendanceVerificationMethod.UKG_INTEGRATION ||
    config.type === AttendanceVerificationMethod.UKG_READY_INTEGRATION ||
    config.type === AttendanceVerificationMethod.ATTENDANCE_ON_DEMAND_INTEGRATION ||
    config.type === AttendanceVerificationMethod.TAKE_A_VIDEO ||
    config.type === AttendanceVerificationMethod.AI_TAKE_A_VIDEO
  );
}

export function getEmptyTutorial(): Tutorial {
  return { stepsClockIn: [], stepsClockOut: [] };
}

export function getEmptyTutorialStep(section: TutorialSection): TutorialStep {
  return { tutorialBody: "", tutorialHeadline: "", tutorialImage: "", tutorialSection: section };
}

export const timeclockLocationStringSchema = z
  .string()
  .min(1, "Required")
  .max(200, "Location must be 200 characters or less");
export const pinsToEnterStringSchema = z.string().max(100, "PINs must be 100 characters or less");

export const KIOSK_VENDOR_OPTIONS = [
  { value: "touchpoint", label: "TouchPoint" },
  { value: "staff-lion", label: "StaffLion" },
] as const;

export type KioskVendor = (typeof KIOSK_VENDOR_OPTIONS)[number]["value"];

/**
 * Single source of truth for the Mesh kioskVendor default, mirroring the backend's own
 * `kioskVendor ?? "touchpoint"` fallback. Used everywhere a Mesh config needs a default
 * instead of each call site repeating the "touchpoint" literal.
 */
export const DEFAULT_KIOSK_VENDOR: KioskVendor = "touchpoint";

export const VALIDATION_MODEL_OPTION_VALUES = {
  UKG: "ukg-timeclock-success",
  HOSTED_TIME: "hosted-time",
  EMPEON: "empeon",
  SELECT: "select",
} as const;

export type ValidationModelOptionValue =
  (typeof VALIDATION_MODEL_OPTION_VALUES)[keyof typeof VALIDATION_MODEL_OPTION_VALUES];

export const VALIDATION_MODEL_OPTIONS: Array<{
  label: string;
  value: ValidationModelOptionValue;
  models: string[];
}> = [
  {
    label: "UKG",
    value: VALIDATION_MODEL_OPTION_VALUES.UKG,
    models: ["ukg-timeclock-success"],
  },
  {
    label: "HostedTime",
    value: VALIDATION_MODEL_OPTION_VALUES.HOSTED_TIME,
    models: ["polygrammic-p100-green-classifier", "polygrammic-p100-blue-mountain-classifier"],
  },
  {
    label: "Empeon",
    value: VALIDATION_MODEL_OPTION_VALUES.EMPEON,
    models: ["empeon-ai-classifier-workflow"],
  },
  {
    label: "Select",
    value: VALIDATION_MODEL_OPTION_VALUES.SELECT,
    models: ["select-classifier-workflow"],
  },
];

export const validationModelOptionSchema = z.enum(
  [
    VALIDATION_MODEL_OPTION_VALUES.UKG,
    VALIDATION_MODEL_OPTION_VALUES.HOSTED_TIME,
    VALIDATION_MODEL_OPTION_VALUES.EMPEON,
    VALIDATION_MODEL_OPTION_VALUES.SELECT,
  ],
  {
    errorMap: () => ({ message: "Required" }),
  }
);

// TODO(TEM-2621): remove cast once @clipboard-health/contract-backend-main exposes validationModels
type AiTakeAVideoConfigWithModels = AiTakeAVideoConfig & { validationModels?: string[] };

/**
 * Mesh config with kioskVendor guaranteed present. Used at the two call sites that build a
 * Mesh config field-by-field (createDefaultMethodConfig, handleVendorChange) rather than by
 * spreading an existing config, so a future edit to either can't silently drop the field.
 */
export type NormalizedMeshIntegrationConfig = MeshIntegrationConfig & {
  kioskVendor: KioskVendor;
};

export function getSelectedValidationModelOption(
  method: AiTakeAVideoConfig
): ValidationModelOptionValue | "" {
  const { validationModels } = method as AiTakeAVideoConfigWithModels;
  const models = isDefined(validationModels)
    ? validationModels.filter((m) => m.trim().length > 0)
    : [];
  if (models.length === 0) {
    return "";
  }
  const match = VALIDATION_MODEL_OPTIONS.find((option) =>
    option.models.every((model) => models.includes(model))
  );
  return match?.value ?? "";
}

export function getModelsForOption(optionValue: ValidationModelOptionValue): string[] {
  const option = VALIDATION_MODEL_OPTIONS.find((o) => o.value === optionValue);
  return option?.models ?? [];
}

export function hasValidationModels(method: AiTakeAVideoConfig): boolean {
  const { validationModels } = method as AiTakeAVideoConfigWithModels;
  return isDefined(validationModels) && validationModels.some((m) => m.trim().length > 0);
}

export const tutorialHeadlineSchema = z
  .string()
  .min(1, "Heading is required")
  .max(100, "Heading must be 100 characters or less");
export const tutorialBodySchema = z
  .string()
  .min(1, "Body is required")
  .max(500, "Body must be 500 characters or less");
export const tutorialImageSchema = z.string().url("Must be a valid URL").or(z.literal(""));

export const tutorialStepFormSchema = z.object({
  tutorialHeadline: tutorialHeadlineSchema,
  tutorialBody: tutorialBodySchema,
  tutorialImage: tutorialImageSchema,
  tutorialSection: z.string(),
});

export type TutorialStepFormValues = z.infer<typeof tutorialStepFormSchema>;

const tutorialFormSchema = z.object({
  stepsClockIn: z.array(tutorialStepFormSchema),
  stepsClockOut: z.array(tutorialStepFormSchema),
});

export const externalIntegrationConfigFormSchema = z.object({
  timeclockLocationString: timeclockLocationStringSchema,
  pinsToEnterString: pinsToEnterStringSchema,
  tutorial: tutorialFormSchema,
});

export type ExternalIntegrationConfigFormValues = z.infer<
  typeof externalIntegrationConfigFormSchema
>;

export const takeAVideoConfigFormSchema = externalIntegrationConfigFormSchema;
export type TakeAVideoConfigFormValues = ExternalIntegrationConfigFormValues;

export const emptyConfigFormSchema = z.object({});
export type EmptyConfigFormValues = z.infer<typeof emptyConfigFormSchema>;

const EXTERNAL_INTEGRATION_TYPES = [
  AttendanceVerificationMethod.UKG_INTEGRATION,
  AttendanceVerificationMethod.UKG_READY_INTEGRATION,
  AttendanceVerificationMethod.ATTENDANCE_ON_DEMAND_INTEGRATION,
  AttendanceVerificationMethod.MESH_INTEGRATION,
];

const VIDEO_TYPES = [
  AttendanceVerificationMethod.TAKE_A_VIDEO,
  AttendanceVerificationMethod.AI_TAKE_A_VIDEO,
];

// eslint-disable-next-line unicorn/prefer-set-has
const CONFIG_TYPES = [...EXTERNAL_INTEGRATION_TYPES, ...VIDEO_TYPES];

export function getMethodValidationSchema(
  type: AttendanceVerificationMethodType
): z.ZodObject<z.ZodRawShape> {
  if (EXTERNAL_INTEGRATION_TYPES.includes(type) || VIDEO_TYPES.includes(type)) {
    return externalIntegrationConfigFormSchema;
  }
  return emptyConfigFormSchema;
}

export function getMethodDefaultValues(
  method: AttendanceVerificationMethodConfig
): ExternalIntegrationConfigFormValues | EmptyConfigFormValues {
  if (CONFIG_TYPES.includes(method.type)) {
    const configMethod = method as
      | UkgIntegrationConfig
      | AttendanceOnDemandIntegrationConfig
      | TakeAVideoConfig
      | AiTakeAVideoConfig;
    return {
      timeclockLocationString: configMethod.timeclockLocationString ?? "",
      pinsToEnterString: configMethod.pinsToEnterString ?? "",
      tutorial: configMethod.tutorial ?? { stepsClockIn: [], stepsClockOut: [] },
    };
  }
  return {};
}

function getDefaultTutorialConfig() {
  return { timeclockLocationString: "", pinsToEnterString: "", tutorial: getEmptyTutorial() };
}

export function createDefaultMethodConfig(
  type: AttendanceVerificationMethodType
): AttendanceVerificationMethodConfig {
  switch (type) {
    case AttendanceVerificationMethod.GEOFENCE: {
      return { type, isEnabled: true };
    }
    case AttendanceVerificationMethod.NFC_TAG: {
      return { type, isEnabled: false };
    }
    case AttendanceVerificationMethod.UKG_INTEGRATION: {
      return { type, isEnabled: false, ...getDefaultTutorialConfig() };
    }
    case AttendanceVerificationMethod.UKG_READY_INTEGRATION: {
      return { type, isEnabled: false, ...getDefaultTutorialConfig() };
    }
    case AttendanceVerificationMethod.ATTENDANCE_ON_DEMAND_INTEGRATION: {
      return { type, isEnabled: false, ...getDefaultTutorialConfig() };
    }
    case AttendanceVerificationMethod.MESH_INTEGRATION: {
      const config: NormalizedMeshIntegrationConfig = {
        type,
        isEnabled: false,
        ...getDefaultTutorialConfig(),
        kioskLocation: "",
        kioskVendor: DEFAULT_KIOSK_VENDOR,
      };
      return config;
    }
    case AttendanceVerificationMethod.TAKE_A_VIDEO: {
      return { type, isEnabled: false, ...getDefaultTutorialConfig() };
    }
    case AttendanceVerificationMethod.AI_TAKE_A_VIDEO: {
      return { type, isEnabled: false, ...getDefaultTutorialConfig() };
    }
    default: {
      return { type: AttendanceVerificationMethod.GEOFENCE, isEnabled: true };
    }
  }
}
